/**
 * The single HTTP seam between the application and the Express backend (F-33, C-6).
 *
 * Every call carries the session cookie, and every mutation carries the CSRF token. There is no
 * second fetch path in this application; a surface that needs the network uses this module, so the
 * credential and CSRF rules cannot be forgotten one screen at a time.
 *
 * C-6: the origin comes from `VITE_API_ORIGIN` at build time. Nothing here reads a secret, and no
 * secret may be placed in a `VITE_*` variable, because everything with that prefix is inlined into
 * the shipped bundle. Legacy `REACT_APP_*` build arguments from the historical Docker workflow are
 * mapped to `VITE_*` at deploy time, which #39 verifies against the built public config.
 *
 * F-33 boundary: the display/kiosk CSRF exemption in the historical backend is NOT a licence for
 * broad device authority. A restricted device session may only reach the routes in
 * DEVICE_ROUTE_RULES; anything else throws before a request leaves the browser. #32 (R8.1) owns
 * the server half of that boundary, and #28 (R6.3) proves both halves with positive and negative
 * tests.
 */

import { assertNoMutation } from '../routes/fixtureMode.js';

const API_ORIGIN = import.meta.env?.VITE_API_ORIGIN ?? '';
const CSRF_COOKIE = 'chorestory.csrf';
const CSRF_HEADER = 'x-csrf-token';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * What a shared display (a v2 DEVICE session, #582 S2) may call — the SERVER's list, copied rule for
 * rule. `server/auth/middleware/auth.js` `DEVICE_ROUTE_POLICY` is the authority and refuses
 * everything else with 403; this copy only makes a screen fail before the request leaves the page.
 * `test/api-client.test.js` parses the server's list and fails when the two differ, so a route
 * cannot open on one side and not the other.
 *
 * Exact templates, never prefixes: `/api/users` must never let `/api/users/7/pin` through. The
 * avatar rule's household check is the server's (`sameTenantSlug`); an `<img>` never passes
 * through this module anyway.
 */
const DEVICE_READ = Object.freeze(['GET', 'HEAD']);
export const DEVICE_ROUTE_RULES = Object.freeze([
  { methods: DEVICE_READ, path: /^\/api\/auth\/verify$/ },
  { methods: DEVICE_READ, path: /^\/api\/auth\/csrf-token$/ },
  { methods: DEVICE_READ, path: /^\/api\/dashboard-data$/ },
  { methods: DEVICE_READ, path: /^\/api\/profiles$/ },
  { methods: DEVICE_READ, path: /^\/api\/profiles\/actor$/ },
  { methods: DEVICE_READ, path: /^\/api\/feed$/ },
  { methods: DEVICE_READ, path: /^\/api\/activity$/ },
  { methods: DEVICE_READ, path: /^\/api\/files\/t\/([a-z0-9-]{3,100})\/avatars\/(?:generated|uploaded)\/[a-z0-9._-]+$/ },
  { methods: ['POST'], path: /^\/api\/auth\/logout$/ },
  { methods: ['POST'], path: /^\/api\/profiles\/[1-9][0-9]{0,9}\/activate$/ },
  { methods: ['POST'], path: /^\/api\/profiles\/actor\/interaction$/ },
  { methods: ['POST'], path: /^\/api\/profiles\/deactivate$/ },
  { methods: ['POST'], path: /^\/api\/chores\/[1-9][0-9]{0,9}\/execute$/ },
  { methods: ['POST'], path: /^\/api\/chores\/executions\/[1-9][0-9]{0,9}\/undo$/ },
  { methods: ['POST'], path: /^\/api\/reassign-chore$/ },
  { methods: ['PUT'], path: /^\/api\/chore-order$/ },
]);

/** The header that names the PIN-proved actor on the four chore writes (`displayActor.js`). */
export const DISPLAY_ACTOR_HEADER = 'x-display-actor';

/**
 * #580 D1 (#903) — the header that binds a family turn's execute and undo to the turn's generation
 * (`server/auth/middleware/familyActor.js`). A stale-request binding, not a credential: CSRF still
 * applies. Never sent by a device, whose own generation is `DISPLAY_ACTOR_HEADER`'s.
 */
export const FAMILY_ACTOR_HEADER = 'x-family-actor';

/**
 * #580 D1 (#902) — what a session holding a FAMILY TURN may call: the server's `TURN_ROUTE_POLICY`
 * (`server/auth/familyTurn.js`), copied rule for rule and read the same way (`turnRequestAllowed`).
 * `revoked`: also open to a revoked session. `active`: only while a member's turn is live. `own`: the
 * id the path captures must be that member. The server refuses everything else for the turn's
 * session, whoever's request it is, so this copy gates the shell's own background requests too
 * (preferences, theme, parent re-check), not only a screen's. `test/api-client.test.js` parses the
 * server's list and fails when the two differ.
 */
const TURN_READ = Object.freeze(['GET', 'HEAD']);
/** #1003: the reads opened after slice 3b grant GET alone, as the server's `GET` set does. */
const TURN_GET = Object.freeze(['GET']);
const TURN_POST = Object.freeze(['POST']);
export const FAMILY_TURN_ROUTE_RULES = Object.freeze([
  { methods: TURN_READ, path: /^\/api\/auth\/verify$/ },
  { methods: TURN_READ, path: /^\/api\/auth\/csrf-token$/, revoked: true },
  { methods: TURN_READ, path: /^\/api\/profiles$/ },
  { methods: TURN_READ, path: /^\/api\/profiles\/actor$/ },
  { methods: TURN_POST, path: /^\/api\/profiles\/[1-9][0-9]{0,9}\/activate$/ },
  { methods: TURN_POST, path: /^\/api\/profiles\/deactivate$/ },
  { methods: TURN_POST, path: /^\/api\/profiles\/actor\/interaction$/ },
  { methods: TURN_POST, path: /^\/api\/auth\/logout$/, revoked: true },
  { methods: TURN_POST, path: /^\/api\/auth\/login$/, revoked: true },
  { methods: TURN_POST, path: /^\/api\/auth\/reset-password$/, revoked: true },
  { methods: TURN_READ, path: /^\/api\/auth\/google$/, revoked: true },
  { methods: TURN_READ, path: /^\/api\/auth\/google\/callback$/, revoked: true },
  { methods: TURN_READ, path: /^\/api\/my-day\/([1-9][0-9]{0,9})$/, active: true, own: true },
  { methods: TURN_READ, path: /^\/api\/wallet\/([1-9][0-9]{0,9})$/, active: true, own: true },
  { methods: TURN_READ, path: /^\/api\/wallet\/([1-9][0-9]{0,9})\/jars$/, active: true, own: true },
  { methods: TURN_READ, path: /^\/api\/wallet\/([1-9][0-9]{0,9})\/saving-goal$/, active: true, own: true },
  { methods: TURN_READ, path: /^\/api\/wallet\/([1-9][0-9]{0,9})\/jar-history$/, active: true, own: true },
  { methods: TURN_READ, path: /^\/api\/wallet\/([1-9][0-9]{0,9})\/chapters\/[1-9][0-9]{0,15}$/, active: true, own: true },
  { methods: TURN_POST, path: /^\/api\/chores\/[1-9][0-9]{0,9}\/execute$/, active: true },
  { methods: TURN_POST, path: /^\/api\/chores\/executions\/[1-9][0-9]{0,9}\/undo$/, active: true },
  // #1003: the member's own approval echo, streak, trophies and story level, and the household's goal and
  // its story. The server REQUIRES the turn's generation on each, so a caller sends `familyGeneration`.
  { methods: TURN_GET, path: /^\/api\/completion-reviews$/, active: true },
  { methods: TURN_GET, path: /^\/api\/village$/, active: true },
  { methods: TURN_GET, path: /^\/api\/village\/story$/, active: true },
  { methods: TURN_GET, path: /^\/api\/trophies\/([1-9][0-9]{0,9})$/, active: true, own: true },
  { methods: TURN_GET, path: /^\/api\/streaks\/([1-9][0-9]{0,9})$/, active: true, own: true },
  { methods: TURN_GET, path: /^\/api\/story-progress\/([1-9][0-9]{0,9})$/, active: true, own: true },
]);

/** The session paths whose success ends or replaces the session, and with it any turn (`revoked` POSTs). */
const SESSION_ENDING = new Set(['/api/auth/login', '/api/auth/logout', '/api/auth/reset-password']);
const SESSION_PATH = '/api/auth/verify';
/** A member picked or put back on the picker: the server writes `locked` first and the member last. */
const SELECTION_PATH = /^\/api\/profiles\/(?:[1-9][0-9]{0,9}\/activate|deactivate)$/;
const TURN_FORBIDDEN_BODY = Object.freeze({ error: 'A family turn is on this device', code: 'FAMILY_TURN_FORBIDDEN' });
const TURN_REVOKED_BODY = Object.freeze({ error: 'This session has ended', code: 'FAMILY_TURN_REVOKED' });
const LOCKED_TURN = Object.freeze({ mode: 'locked', memberId: null });
const REVOKED_TURN = Object.freeze({ mode: 'revoked', memberId: null });

/**
 * The two credential paths the SERVER exempts from CSRF (`server/auth/middleware/csrf.js`), #582.
 *
 * A display being paired holds no session, so it holds no CSRF cookie, and the rule in `send`
 * would refuse its claim before it left the page. Its credential is the pairing code, or the
 * display token the code returned. Matched EXACTLY, never as a prefix, and a cookie that does exist
 * is still sent. `test/api-client.test.js` ties this list to the server's.
 */
export const CSRF_EXEMPT_PATHS = Object.freeze([
  '/api/display-tokens/exchange',
  '/api/displays/claim',
]);

export class ApiError extends Error {
  constructor(message, { status, path, body }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.path = path;
    this.body = body;
  }
}

export class DeviceScopeError extends Error {
  constructor(path) {
    super(`a restricted device session may not call ${path}`);
    this.name = 'DeviceScopeError';
    this.path = path;
  }
}

/**
 * A request the page's family turn does not allow, refused before it leaves the page. It carries the
 * refusal the SERVER gives for it (403 `FAMILY_TURN_FORBIDDEN`, or 401 `FAMILY_TURN_REVOKED` once the
 * turn is revoked), so every caller handles it as it already handles the server's: the session reader
 * still reads a revoked turn, and a live re-read stops at a 403.
 */
export class FamilyTurnScopeError extends ApiError {
  constructor(path, revoked) {
    super(`a family turn may not call ${path}`, {
      status: revoked ? 401 : 403,
      path,
      body: revoked ? TURN_REVOKED_BODY : TURN_FORBIDDEN_BODY,
    });
    this.name = 'FamilyTurnScopeError';
  }
}

function readCsrfToken() {
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${CSRF_COOKIE}=`));
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : null;
}

function assertDeviceScope(path, method, scope) {
  if (scope !== 'device') return;
  const bare = String(path).split('?')[0];
  const allowed = DEVICE_ROUTE_RULES.some((rule) => rule.methods.includes(method) && rule.path.test(bare));
  if (!allowed) throw new DeviceScopeError(path);
}

/**
 * The family turn this page's session holds, as the server last described it: `null` for none, or
 * `{ mode, memberId }`. Page state, because a turn belongs to the session cookie, not to a screen.
 *
 * It is learned HERE, from the answers themselves, and not from the actor context: a child's return
 * (`ChildActorReturn`) clears the context before it asks the server again, and a background request
 * in that gap must still be refused. It narrows on any answer that describes a turn, or on the
 * server's turn refusal. It widens only on the session endpoint naming an account with no turn, or on
 * a sign-in or sign-out, which replaces the session.
 *
 * Answers arrive in any order, so each request is stamped when it leaves the page (`send`) and its
 * answer is weighed against that stamp:
 *
 * - `sessionEpoch` counts the page's sessions. An answer from a session that a sign-in, a sign-out or
 *   a password reset has since replaced changes nothing, a revoked refusal included.
 * - `sendSeq` orders the requests. An answer is learned only if its request left after the request
 *   whose answer was learned last (`learnedSeq`), whether or not that answer changed the scope: the
 *   same member picked again is a new turn with the same scope.
 * - A revoked refusal applies whenever it arrives, and a revoked turn stays revoked until a sign-in, a
 *   sign-out or a password reset ends its session.
 * - While a selection (`SELECTION_PATH`) is in flight, no other answer is learned: the server can
 *   answer one between its `locked` write and the member's, and the selection's own answer says how it
 *   ended.
 */
let familyTurnScope = null;
let sessionEpoch = 0;
let sendSeq = 0;
let learnedSeq = 0;
/** The stamps of the selections in flight; one from a replaced session holds nothing back. */
const selectionsInFlight = new Set();

/** Fails closed: anything but a readable version-2 active turn names no member. */
function turnScopeOf(descriptor) {
  if (!descriptor || typeof descriptor !== 'object' || descriptor.version !== 2) return LOCKED_TURN;
  if (descriptor.mode === 'revoked') return REVOKED_TURN;
  const memberId = descriptor.member?.userId;
  if (descriptor.mode !== 'active' || typeof descriptor.generation !== 'string' || !descriptor.generation
      || memberId === undefined || memberId === null) return LOCKED_TURN;
  return { mode: 'active', memberId: String(memberId) };
}

/** A user id is a positive integer (`users.id` is a serial), as a number or a string of digits. */
function isAccountId(id) {
  const n = typeof id === 'string' && /^[0-9]+$/.test(id) ? Number(id) : id;
  return Number.isSafeInteger(n) && n > 0;
}

/** A session answer widens only when it names an account: an empty `user` describes nobody. */
function namesAccount(user) {
  return Boolean(user) && typeof user === 'object' && [user.id, user.userId].some(isAccountId);
}

/** `sent`: the stamp `send` gave the request, `{ seq, session, selection }`. */
function learnFamilyTurn(path, method, response, parsed, sent) {
  if (sent.session !== sessionEpoch) return;
  if (response.status === 401 && parsed?.code === 'FAMILY_TURN_REVOKED') {
    familyTurnScope = REVOKED_TURN;
    learnedSeq = Math.max(learnedSeq, sent.seq);
    return;
  }
  if (sent.seq < learnedSeq) return;
  const bare = String(path).split('?')[0];
  if (response.ok && method === 'POST' && SESSION_ENDING.has(bare)) {
    familyTurnScope = null;
    sessionEpoch += 1;
    learnedSeq = sent.seq;
    return;
  }
  if (familyTurnScope?.mode === 'revoked') return;
  if (!sent.selection && [...selectionsInFlight].some((s) => s.session === sessionEpoch)) return;
  let next;
  if (response.ok && parsed && typeof parsed === 'object' && 'familyTurn' in parsed) {
    next = turnScopeOf(parsed.familyTurn);
  } else if (response.ok && method === 'GET' && bare === SESSION_PATH && namesAccount(parsed?.user)) {
    next = null;
  } else if (response.status === 403 && parsed?.code === 'FAMILY_TURN_FORBIDDEN' && !familyTurnScope) {
    // Another tab on this cookie started a turn: refused once by the server, then here.
    next = LOCKED_TURN;
  } else {
    return;
  }
  familyTurnScope = next;
  learnedSeq = sent.seq;
}

/** The server's `turnRequestAllowed`, against what this page last learned. */
function assertFamilyTurnScope(path, method) {
  const turn = familyTurnScope;
  if (!turn) return;
  const bare = String(path).split('?')[0];
  const allowed = FAMILY_TURN_ROUTE_RULES.some((rule) => {
    if (!rule.methods.includes(method)) return false;
    const match = rule.path.exec(bare);
    if (!match) return false;
    if (turn.mode === 'revoked') return rule.revoked === true;
    if (rule.active && turn.mode !== 'active') return false;
    return !rule.own || (turn.memberId !== null && match[1] === turn.memberId);
  });
  if (!allowed) throw new FamilyTurnScopeError(path, turn.mode === 'revoked');
}

/**
 * #578 AD-05 — the server's answer to a parent change made with an old parent proof
 * (`server/auth/parentProof.js`, `PARENT_PROOF_REQUIRED.code`).
 */
export const PARENT_REAUTH_CODE = 'PARENT_REAUTH_REQUIRED';

let parentRecheckHandler = null;

/**
 * Register the ONE prompt that asks a parent to re-check. The handler resolves `true` when the
 * parent proved it is them (the window is open again) and `false` when they cancelled.
 *
 * @param {() => Promise<boolean>} handler
 * @returns {() => void} release — unregisters this handler, and only this one
 */
export function setParentRecheckHandler(handler) {
  parentRecheckHandler = handler;
  return () => {
    if (parentRecheckHandler === handler) parentRecheckHandler = null;
  };
}

function isParentRecheck(error) {
  return error instanceof ApiError && error.status === 403 && error.body?.code === PARENT_REAUTH_CODE;
}

/**
 * @param {string} path      an absolute API path, for example `/api/quests`
 * @param {object} [options]
 * @param {string} [options.method='GET']
 * @param {unknown} [options.body]   serialized as JSON when present
 * @param {'parent'|'child'|'device'|'operator'} [options.scope='parent']
 * @param {AbortSignal} [options.signal]
 * @param {string} [options.actorGeneration]  device scope only: sent as `X-Display-Actor`
 * @param {string} [options.familyGeneration]  never for a device: sent as `X-Family-Actor`
 * @param {boolean} [options.keepalive]  lets a best-effort request outlive the page (pagehide)
 */
export async function request(path, options = {}) {
  try {
    return await send(path, options);
  } catch (error) {
    // One prompt, then ONE retry of the same request. A second refusal is thrown, never
    // re-prompted: a loop here would trap a parent whose re-check the server did not accept.
    const handler = parentRecheckHandler;
    if (!isParentRecheck(error) || !handler) throw error;
    // Replay only under the sign-in that was refused. Every sign-in mints a new CSRF cookie, so a
    // changed cookie means somebody signed in (perhaps as another family) while the prompt was open.
    const signIn = readCsrfToken();
    const rechecked = await handler();
    if (!rechecked || readCsrfToken() !== signIn) throw error;
    return send(path, options);
  }
}

async function send(path, options) {
  const { method = 'GET', body, scope = 'parent', signal, actorGeneration, familyGeneration, keepalive } = options;
  assertDeviceScope(path, method, scope);
  assertFamilyTurnScope(path, method);

  const headers = { accept: 'application/json' };
  // #577: a photo upload is multipart. The browser writes that content type (with its boundary)
  // itself, so a FormData body gets no content-type here and is sent as it is.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['content-type'] = 'application/json';
  if (scope === 'device' && typeof actorGeneration === 'string' && actorGeneration) {
    headers[DISPLAY_ACTOR_HEADER] = actorGeneration;
  }
  if (scope !== 'device' && typeof familyGeneration === 'string' && familyGeneration) {
    headers[FAMILY_ACTOR_HEADER] = familyGeneration;
  }

  if (MUTATING.has(method)) {
    // The fixture gallery promises zero network mutations. Enforce it here, at the one seam every
    // request passes through, rather than asking 18 surfaces to remember.
    assertNoMutation(path, method);
    const token = readCsrfToken();
    // A device may send without the cookie: the server exempts display sessions from CSRF
    // (`server/auth/middleware/csrf.js`), because a kiosk on another origin cannot read it. The
    // device allowlist above has already refused anything outside the board's own routes.
    if (!token && scope !== 'device' && !CSRF_EXEMPT_PATHS.includes(path)) {
      throw new ApiError('missing CSRF token; the session is not established', {
        status: 0,
        path,
        body: null,
      });
    }
    if (token) headers[CSRF_HEADER] = token;
  }

  const sent = {
    seq: (sendSeq += 1),
    session: sessionEpoch,
    selection: method === 'POST' && SELECTION_PATH.test(String(path).split('?')[0]),
  };
  if (sent.selection) selectionsInFlight.add(sent);
  let parsed = null;
  let response;
  try {
    response = await fetch(`${API_ORIGIN}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      signal,
      ...(keepalive ? { keepalive: true } : {}),
    });

    const text = await response.text();
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = { raw: text };
      }
    }
    learnFamilyTurn(path, method, response, parsed, sent);
  } finally {
    // A selection that fails or is aborted settles too, so it cannot leave the page deaf.
    selectionsInFlight.delete(sent);
  }

  if (!response.ok) {
    throw new ApiError(`${method} ${path} failed with ${response.status}`, {
      status: response.status,
      path,
      body: parsed,
    });
  }
  return parsed;
}

/**
 * The absolute URL for an API path, for the cases the `fetch` wrapper cannot serve.
 *
 * A browser REDIRECT is one: the Google sign-in flow is a server-side redirect
 * (`server/index.js:156` mounts it at `/api/auth/google`), so the browser must NAVIGATE there
 * rather than fetch it. Building that URL by hand in a container would be a second place that
 * knows about `VITE_API_ORIGIN`, and the second place is the one that goes stale.
 */
export function apiUrl(path) {
  return `${API_ORIGIN}${path}`;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};
